import { useAtomValueRawSync } from 'jotai';
import { useEffect, useState } from 'react';

import { api } from '@/api';
import { createPreviewFetcher } from '@/lib/previewFetcher';
import { cachePreviewAtom, cameraPreviewAtom } from '@/state/api/cameraPreviewAtoms';
import { onlineCamerasAtom } from '@/state/api/devicesAtoms';
import type { StoredPreview } from '@/types/devices';
import type { Store } from '@/types/store';

interface OwnedFetcher {
    store: Store;
    fetcher: ReturnType<typeof createPreviewFetcher>;
}

function createStoreFetcher(store: Store): ReturnType<typeof createPreviewFetcher> {
    return createPreviewFetcher({
        fetchPreview: (id, serverId, signal): Promise<Blob | null> =>
            api.getDevicePreview(id, { serverId, signal }),
        readPreview: (id): StoredPreview | null => store.get(cameraPreviewAtom(id)),
        write: (id, dataUrl): void => store.set(cachePreviewAtom, id, dataUrl),
    });
}

/**
 * Keeps the preview images of `store`'s cameras current: every camera seen coming online — in
 * a page load's status replay, in a reconnect's, or on its own — has its preview fetched
 * unless a recent one is held. StoreProvider calls it once per store.
 */
export function useCameraPreviewRefresh(store: Store): void {
    const onlineCameras = useAtomValueRawSync(onlineCamerasAtom, { store });
    // The online set the last render saw, with the cameras that entered it. A new store has
    // seen none of its cameras.
    const [seen, setSeen] = useState({ store, online: onlineCameras, entered: onlineCameras });
    if (seen.store !== store || seen.online !== onlineCameras) {
        const before = new Set(seen.store === store ? seen.online.map((camera) => camera.id) : []);
        setSeen({ store, online: onlineCameras, entered: onlineCameras.filter((camera) =>
            !before.has(camera.id)) });
    }
    const { entered: enteredOnlineCameras } = seen;
    // One fetcher for each store, so the queue of a store stops when the store goes.
    const [ownedFetcher, setOwned] = useState((): OwnedFetcher => ({
        store, fetcher: createStoreFetcher(store),
    }));
    if (ownedFetcher.store !== store) {
        setOwned({ store, fetcher: createStoreFetcher(store) });
    }
    const { fetcher } = ownedFetcher;

    useEffect((): (() => void) => (): void => fetcher.abort(), [fetcher]);

    useEffect(() => {
        fetcher.prefetch(enteredOnlineCameras);
    }, [fetcher, enteredOnlineCameras]);
}
